/*
 * <BeSegmentedProgressBar> -- a multi-colour *segmented* progress bar: the
 * GitHub "language breakdown" / Primer "multiple segments" idea, as a BeOS-
 * styled sibling of <BeProgressBar>. See components/segmented-progress-bar.css
 * for the look + the shift_color->color-mix bevel derivation (Notes/23).
 *
 * NOTE: real BeOS BStatusBar was single-colour; this is an opt-in extension. The
 * single-value bar is still <BeProgressBar>.
 *
 *   <BeSegmentedProgressBar
 *     label="Epic" trailing="18 / 24" legend
 *     segments={[
 *       { value: 12, tone: "done",    label: "Done" },    // standardized status colour
 *       { value: 4,  tone: "active",  label: "Active" },
 *       { value: 2,  tone: "blocked", label: "Blocked" },
 *       { value: 6,  label: "Pending" },                  // no colour -> auto categorical
 *     ]}
 *   />
 *
 * Each segment's colour is `color` (explicit, any CSS colour) > `tone` (a
 * standardized status name) > an auto-assigned categorical colour (var(--be-cat-N)).
 * Pass a `max` larger than the sum of the values to leave a white remainder (a
 * partially-filled multi-phase bar); by default the segments fill the trough.
 */

import { BeTooltip } from "./BeTooltip.tsx";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";
import type { BeProgressSegment, BeStatusTone } from "./status.ts";

export type { BeProgressSegment, BeStatusTone };

/** Size of the categorical sequence (--be-cat-1..N) before it cycles. */
const CATEGORICAL_COUNT = 8;

export type BeSegmentedProgressBarProps = BeProps<
	"div",
	{
		/** The coloured runs, left to right. */
		segments: BeProgressSegment[];
		/**
		 * Denominator for the widths. Defaults to the sum of the segment values
		 * (segments fill the whole trough). A value greater than that sum leaves a
		 * white empty remainder, like a partially-complete bar.
		 */
		max?: number;
		/** Left-aligned label above the trough. */
		label?: string;
		/** Right-aligned trailing text above the trough. */
		trailing?: string;
		/** Show a GitHub-style legend (swatch + label + percent) below the bar. */
		legend?: boolean;
		/**
		 * Opt-in: show a BeOS bubble-help tooltip on each segment with its
		 * "<label>: <value> (<pct>)" (or the segment's own `tip`). Wraps each
		 * segment in <BeTooltip>. Default off.
		 */
		segmentTooltips?: boolean;
		/** Release the preferred width and span the parent's inline size. */
		fluid?: boolean;
	}
>;

function pctString(n: number): string {
	// keep one decimal only when it matters, so "55%" not "55.0%"
	const r = Math.round(n * 10) / 10;
	return `${Number.isInteger(r) ? r : r.toFixed(1)}%`;
}

/** Default per-segment tooltip text: "<label>: <value> (<pct>)". */
function segmentTipText(name: string | undefined, value: number, pctText: string): string {
	return `${name ? `${name}: ` : ""}${value} (${pctText})`;
}

export const BeSegmentedProgressBar = beComponent<BeSegmentedProgressBarProps, HTMLDivElement>("BeSegmentedProgressBar", (props, be) => {
	const { segments, max, label, trailing, legend, scale, fluid, className, style, segmentTooltips, ...rest } = props;

	const sum = segments.reduce((a, s) => a + Math.max(0, s.value), 0);
	const denom = Math.max(sum, max ?? 0) || 1;

	// resolve each segment's colour: explicit `color` > `tone` token > the
	// standardized categorical sequence, auto-assigned in order and cycled.
	let autoIdx = 0;
	const parts = segments.map((s) => {
		const pct = (Math.max(0, s.value) / denom) * 100;
		let color: string;
		if (s.color) color = s.color;
		else if (s.tone) color = `var(--be-status-${s.tone})`;
		else color = `var(--be-cat-${(autoIdx++ % CATEGORICAL_COUNT) + 1})`;
		return {
			...s,
			color,
			name: s.label ?? s.tone,
			pct,
			pctText: pctString(pct),
		};
	});

	const ariaLabel = parts.map((p) => `${p.name ? `${p.name} ` : ""}${p.pctText}`).join(", ");

	return (
		<div
			role="img"
			aria-label={label ? `${label}: ${ariaLabel}` : ariaLabel}
			{...rest}
			{...be("be-segmented-progress-bar", fluid && "is-fluid")}
		>
			{(label != null || trailing != null) && (
				<div className="be-progress-text">
					{pixelText(label, "be-progress-text__label")}
					<span className="be-progress-text__trailing">{pixelText(trailing)}</span>
				</div>
			)}
			<div className="be-segmented-progress-bar__trough">
				<div className="be-segmented-progress-bar__bar" aria-hidden="true">
					{parts.map((p, i) => {
						const segment = (
							<div
								// biome-ignore lint/suspicious/noArrayIndexKey: segment order is the identity
								key={i}
								className="be-segmented-progress-bar__segment"
								// when wrapped for a tooltip the .be-tip-anchor carries the
								// width, so no percentage is emitted and the sheet's fallback
								// fills the anchor
								style={{ "--seg-color": p.color, ...(segmentTooltips ? null : { "--seg-pct": `${p.pct}%` }) }}
							/>
						);
						return segmentTooltips ? (
							<BeTooltip
								// biome-ignore lint/suspicious/noArrayIndexKey: segment order is the identity
								key={i}
								// its OWN name: --seg-pct would inherit onto the segment and kill its var() fallback
								style={{ "--seg-basis": `${p.pct}%` }}
								tip={p.tip ?? segmentTipText(p.name, p.value, p.pctText)}
							>
								{segment}
							</BeTooltip>
						) : (
							segment
						);
					})}
				</div>
			</div>
			{legend && (
				<ul className="be-segmented-progress-bar__legend">
					{parts.map((p, i) => (
						<li
							// biome-ignore lint/suspicious/noArrayIndexKey: segment order is the identity
							key={i}
							className="be-segmented-progress-bar__legend-item"
						>
							<span className="be-swatch is-mini be-segmented-progress-bar__legend-swatch" style={{ "--seg-color": p.color }} />
							{p.name != null && pixelText(p.name)}
							{pixelText(p.pctText, "be-segmented-progress-bar__legend-pct")}
						</li>
					))}
				</ul>
			)}
		</div>
	);
});
